import { Address, BuyingGroupMeta, DealerMeta, DistributionEmail, DistributorMeta, OperationResult, QuotePrefix, QuoteStatusTypes, QuoteTypeNames, RepresentativeMeta } from './powershadesApiTypes';

const QuoteOrderStatus: { [key: string]: QuoteStatusTypes } = {
	ActiveQuote: 'active_quote',
	ApprovedOrder: 'approved_order',
	MfgAcceptedOrder: 'mfg_accepted_order',
	MfgShippedOrder: 'mfg_shipped_order',
	OrderPendingApproval: 'order_pending_approval',
	SubmittedQuote: 'submitted_quote',
	UnderReview: 'under_review',
};

const QuoteTypeNamesById: { [key: number]: QuoteTypeNames } = {
	1: 'RMA',
	2: 'Rework',
	3: 'Bidspec',
	4: 'Internal',
	5: 'CEDIA',
	6: 'Demo',
	7: 'Window Covering'
};

const QuotePrefixById: { [key: number]: QuotePrefix } = {
	1: 'RMA',
	2: 'RWK',
	3: 'BID',
	4: 'INT',
	5: 'PS',
	6: 'DEMO',
	7: 'WC'
};

const GroupTypeOptions = ['dealers', 'distributors', 'territories', 'representatives', 'users', 'organizations'] as const;
type GroupTypes = typeof GroupTypeOptions[number];

const DealerFilterOptions = ['all', 'approved', 'pending', 'declined'] as const;
type DealerFilters = typeof DealerFilterOptions[number];

const QuoteFilterOptions = ['all', 'active', 'placed', 'RMA', 'rework', 'archived'] as const;
type QuoteFilters = 'all' | 'active' | 'placed' | 'RMA' | 'rework' | 'demo' | 'archived';

const UserFilterOptions = ['all', 'admin', 'user'] as const;
type UserFilters = 'all' | 'admin' | 'user';

const EmailDistributionOptions = ['all', 'order placed access', 'invoice access'] as const;
type EmailDistributionFilters = 'all' | 'order placed access' | 'invoice access';

type PossibleFilters = DealerFilters | QuoteFilters | UserFilters | EmailDistributionFilters;

const emptyDealerMeta: DealerMeta = {
	id: 0,
	name: '',
	email: '',
	phone: '',
	status: 'approved',
	allow_credit_card_payments: false,
	has_credit_terms: false,
	channel_id: 5,
	sales_tax_exempt_status: 'non_exempt',
	sales_tax_exempt_reason: 'non_exempt',
	sales_tax_exempt_certificate_location: '',
	sales_tax_exempt_certificate_number: '',
	federal_tax_id: '',
	federal_tax_certificate_location: '',
	territory_ids: []
};

const emptyDistributorMeta: DistributorMeta = {
	id: 0,
	name: '',
	email: '',
	phone: '',
	sales_type: '',
	channel_id: 0,
	territory_ids: [],
	is_distributor_locations_active: false,
};

const emptyRepresentativeMeta: RepresentativeMeta = {
	id: 0,
	name: '',
	email: '',
	phone: '',
	invite_code: '',
	territory_ids: []
};

const emptyDistributionEmail: DistributionEmail = {
	email: '',
	is_receiving_order_placed_emails: false,
	is_receiving_invoice_emails: false
};

const emptyBuyingGroup: BuyingGroupMeta = {
	id: 0,
	name: '',
	description: '',
};

const emptyAddress: Address = {
	first_name: '',
	last_name: '',
	email: '',
	phone: '',
	address: '',
	address2: '',
	city: '',
	state_code: '',
	state: '',
	zip: '',
	country: ''
};

const createOperationResult = (success: boolean, message: string): OperationResult => ({
	success,
	message,
});

const successResult = (message: string) => createOperationResult(true, message);

const errorResult = (message: string) => createOperationResult(false, message);

export type ShadePart = {
	sku: string;
	name: string;
	qty: string;
	uom: string;
	category: string;
	is_active: boolean;
	is_hardware_kit_item: boolean;
	description: string;
	cost_rate: number;
	msrp_rate: number;
};

export type ServiceShadeProperties =
	| "is_motor_only"
	| "is_tube_and_fabric_only"
	| "is_header_extrusion_only"
	| "is_channels_only"
	| "is_hardware_only"
	| "is_shade_service"
	| "is_crown_and_drive_only"
	| "is_shade_service";

export type AssemblyFieldBooleans = "IsMotorOnly"
	| "IsTubeAndFabricOnly"
	| "IsShadeService"
	| "IsHeaderExtrusionOnly"
	| "IsChannelsOnly"
	| "IsHardwareOnly"
	| "IsCrownAndDrive"
	| "MakeThisShade";

export type AssemblyFieldStrings = "RecipeSku"
	| "TubeOverride"
	| "MotorOverride";

export type AssemblyFieldCutBooleans = "IsHeaderExtrusionToBeCut"
	| "IsTubeToBeCut"
	| "IsSillToBeCut"
	| "IsSideToBeCut"
	| "IsFabricToBeCut"
	| "IsHembarToBeCut";

export type AssemblyFieldNames = AssemblyFieldBooleans | AssemblyFieldStrings | AssemblyFieldCutBooleans;

export const assemblyFieldToPropertyNameMap: { [key in AssemblyFieldNames]: ServiceShadeProperties | null } = {
	IsMotorOnly: "is_motor_only",
	IsTubeAndFabricOnly: "is_tube_and_fabric_only",
	IsShadeService: null,
	IsChannelsOnly: "is_channels_only",
	IsHardwareOnly: "is_hardware_only",
	IsHeaderExtrusionOnly: "is_header_extrusion_only",
	IsCrownAndDrive: "is_crown_and_drive_only",
	RecipeSku: null,
	TubeOverride: null,
	MotorOverride: null,
	IsHeaderExtrusionToBeCut: null,
	IsTubeToBeCut: null,
	IsSillToBeCut: null,
	IsSideToBeCut: null,
	IsFabricToBeCut: null,
	MakeThisShade: null,
	IsHembarToBeCut: null
} as const;

export type DisplayNames =
	| "Motor"
	| "Side/Sill Channels"
	| "Header Extrusion"
	| "Hardware"
	| "Crowns and Drives"
	| "Tube and Fabric"
	| "Service";

export const propertyToDisplayNameMap: {
	[key in ServiceShadeProperties]: DisplayNames;
} = {
	is_motor_only: "Motor",
	is_channels_only: "Side/Sill Channels",
	is_header_extrusion_only: "Header Extrusion",
	is_hardware_only: "Hardware",
	is_crown_and_drive_only: "Crowns and Drives",
	is_tube_and_fabric_only: "Tube and Fabric",
	is_shade_service: "Service",
} as const;

export type PropertyNames = keyof typeof propertyToDisplayNameMap;
export type PropertyDisplayNames = typeof propertyToDisplayNameMap[PropertyNames];

export const propertyToPartCategoryMap: {
	[key in ServiceShadeProperties]: DisplayNames;
} = {
	is_motor_only: "Motor",
	is_channels_only: "Side/Sill Channels",
	is_header_extrusion_only: "Header Extrusion",
	is_hardware_only: "Hardware",
	is_crown_and_drive_only: "Motor",
	is_tube_and_fabric_only: "Tube and Fabric",
	is_shade_service: "Service",
} as const;

export const partCategoryToPropertyMap: {
	[key in DisplayNames]: ServiceShadeProperties;
} = {
	Motor: "is_motor_only",
	"Crowns and Drives": "is_crown_and_drive_only",
	"Side/Sill Channels": "is_channels_only",
	"Header Extrusion": "is_header_extrusion_only",
	Hardware: "is_hardware_only",
	"Tube and Fabric": "is_tube_and_fabric_only",
	Service: "is_shade_service",
} as const;

export type PropertyPartCategoryNames = keyof typeof propertyToPartCategoryMap;
export type PropertyDisplayCategoryNames = typeof propertyToPartCategoryMap[PropertyPartCategoryNames];

export type PartCategoryNames =
	| "is_motor_only"
	| "is_channels_only"
	| "is_header_only"
	| "is_hardware_kit_only"
	| "is_crown_and_drive_only"
	| "is_tube_and_fabric_only";

export type PartCategories = {
	[key in PartCategoryNames]: string[];
};

const DealerUserRoles = ['dealer_admin', 'dealer_user', 'dealer_salesperson', 'dealer_purchasing'] as const;

const DistributorUserRoles = ['distributor_admin', 'distributor_user'] as const;

const DistributorLocationRoles = ['distributor_location_admin', 'distributor_location_user'] as const;

const RepresentativeUserRoles = ['rep_admin', 'rep_user'] as const;

const ManufacturerUserRoles = ['manufacturer_admin', 'manufacturer_user'] as const;

const OrganizationUserRoles = ['organization_admin', 'organization_user', 'org_user', 'org_admin', 'org_scheduler'] as const;

const PropertyUserRoles = ['property_admin', 'property_user', 'property_scheduler'] as const;

export const UserRoleNames = [...DealerUserRoles, ...DistributorUserRoles, ...DistributorLocationRoles, ...RepresentativeUserRoles, ...ManufacturerUserRoles, ...OrganizationUserRoles, ...PropertyUserRoles, 'powershades_admin'] as const;

export type PartsListResponse = {
	recipe_sku: string;
	full_parts: ShadePart[];
	actual_parts: ShadePart[];
	part_categories: PartCategories;
};

export {
	QuoteOrderStatus,
	QuoteTypeNamesById,
	QuotePrefixById,
	GroupTypeOptions,
	DealerFilterOptions,
	QuoteFilterOptions,
	UserFilterOptions,
	EmailDistributionOptions,
	emptyDealerMeta,
	emptyDistributorMeta,
	emptyRepresentativeMeta,
	emptyDistributionEmail,
	emptyBuyingGroup,
	emptyAddress,
	createOperationResult,
	successResult,
	errorResult
};

export type {
	GroupTypes, DealerFilters, QuoteFilters, UserFilters, PossibleFilters
};
